import { defineModel } from "../define-model";

export type RunStatus =
	| "queued"
	| "running"
	| "succeeded"
	| "failed"
	| "cancelled"
	| "skipped"
	| "waiting";

export type RunTrigger = "manual" | "schedule" | "assistant" | "api" | "event" | "agent";

/** Synced without the graph snapshot (server `serialize` drops it). */
export type FlowRun = {
	id: string;
	workspaceId: string;
	projectId: string;
	flowId: string;
	/** Published version that ran; 0 = the draft (manual and assistant runs). */
	flowVersion: number;
	trigger: RunTrigger;
	scheduleId: string | null;
	triggeredById: string | null;
	/** Absent on records cached before agents/events existed. */
	agentId?: string | null;
	agentRunId?: string | null;
	eventId?: string | null;
	/** `waiting` = only approvals are left (see node runs' `waitingFor`). */
	status: RunStatus;
	inputs: Record<string, unknown>;
	outputs: Record<string, unknown>;
	error: string | null;
	costUsd: number;
	millicredits: number;
	startedAt: string | null;
	finishedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

declare module "../types" {
	interface SyncModels {
		flowRun: FlowRun;
	}
}

/** Something is executing: new runs wait, the canvas polls fast. Waiting runs don't count. */
export const isActiveRun = (run: Pick<FlowRun, "status"> | undefined) =>
	run?.status === "queued" || run?.status === "running";

export const model = defineModel({
	name: "flowRun",
	load: "lazy",
	indexes: ["flowId"],
	sort: (a, b) => b.createdAt.localeCompare(a.createdAt),
});
